import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { CSSProperties } from 'react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelM, LabelS } from '@sorare/core/src/atoms/typography';
import { ResponsiveImg } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { getRarityRGBColor } from '@sorare/core/src/lib/colors';

import { RewardOverviewChip } from '#src/components/rewards/RewardOverview/index.tsx';
import {
  DeliverableItemOrderFormDialog,
  deliverableItemOrderFragment,
} from '#src/components/rewards/deliverableItem/DeliverableItemOrderFormDialog/index.tsx';

import type {
  DeliverableItemBlock_deliverableItemOrder,
  DeliverableItemBlock_deliverableItemReward,
} from './__generated__/index.graphql.ts';

const FullWidthHorizontal = styled(Horizontal)`
  width: 100%;
  height: var(--quadruple-unit);
  justify-content: space-between;
`;
const DeliverableItemsWrapper = styled(Vertical).attrs({ gap: 0.5 })`
  padding: var(--double-unit);
  border-radius: var(--double-unit);
  background: var(--c-nd-50);
`;

type Props = {
  deliverableItems: DeliverableItemBlock_deliverableItemReward[];
  deliverableItemOrders: DeliverableItemBlock_deliverableItemOrder[];
};
export const DeliverableItemBlock = ({
  deliverableItems,
  deliverableItemOrders,
}: Props) => {
  const [claimedDeliverableItemOrderIds, setClaimedDeliverableItemOrderIds] =
    useState<string[]>([]);
  const [deliverableItemOrderToConfirm, setDeliverableItemOrderToConfirm] =
    useState<DeliverableItemBlock_deliverableItemOrder | null>(null);

  if (!deliverableItems.length) {
    return null;
  }

  return (
    <>
      <Vertical>
        <LabelM bold color="var(--c-nd-600)">
          <FormattedMessage
            id="RewardsRecap.deliverableItem.title"
            defaultMessage="Deliverable Items"
          />
        </LabelM>
        <DeliverableItemsWrapper>
          {deliverableItems.map(({ deliverableItem }) => {
            const correspondingDeliverableItemOrder =
              deliverableItemOrders.find(
                deliverableItemOrder =>
                  deliverableItemOrder.deliverableItem.slug ===
                  deliverableItem?.slug
              );
            const edit =
              correspondingDeliverableItemOrder &&
              claimedDeliverableItemOrderIds.includes(
                correspondingDeliverableItemOrder.id
              );

            return (
              <FullWidthHorizontal key={deliverableItem?.slug}>
                <Horizontal>
                  <RewardOverviewChip
                    style={
                      {
                        '--color': getRarityRGBColor(Rarity.unique),
                      } as CSSProperties
                    }
                  >
                    {!!correspondingDeliverableItemOrder?.deliverableItem
                      .pictureUrl && (
                      <ResponsiveImg
                        src={
                          correspondingDeliverableItemOrder.deliverableItem
                            .pictureUrl
                        }
                        alt=""
                        width={20}
                      />
                    )}
                    <LabelS>x1</LabelS>
                  </RewardOverviewChip>
                  {!!deliverableItem?.name && (
                    <LabelM>{deliverableItem.name}</LabelM>
                  )}
                </Horizontal>
                {!!correspondingDeliverableItemOrder && (
                  <Button
                    color={edit ? 'tertiary' : 'primary'}
                    size="small"
                    onClick={() =>
                      setDeliverableItemOrderToConfirm(
                        correspondingDeliverableItemOrder
                      )
                    }
                  >
                    {edit ? (
                      <FormattedMessage
                        id="RewardsRecap.deliverableItem.editAddress"
                        defaultMessage="Edit address"
                      />
                    ) : (
                      <FormattedMessage
                        id="RewardsRecap.deliverableItem.addAddress"
                        defaultMessage="Add address"
                      />
                    )}
                  </Button>
                )}
              </FullWidthHorizontal>
            );
          })}
        </DeliverableItemsWrapper>
      </Vertical>
      {!!deliverableItemOrderToConfirm && (
        <DeliverableItemOrderFormDialog
          open
          deliverableItemOrder={deliverableItemOrderToConfirm}
          onCancel={() => setDeliverableItemOrderToConfirm(null)}
          onDone={() => {
            setClaimedDeliverableItemOrderIds(array => [
              ...array,
              deliverableItemOrderToConfirm.id,
            ]);
            setDeliverableItemOrderToConfirm(null);
          }}
        />
      )}
    </>
  );
};

DeliverableItemBlock.fragments = {
  deliverableItemReward: gql`
    fragment DeliverableItemBlock_deliverableItemReward on DeliverableItemReward {
      id
      deliverableItem {
        slug
        name
      }
    }
  ` as TypedDocumentNode<DeliverableItemBlock_deliverableItemReward>,
  deliverableItemOrder: gql`
    fragment DeliverableItemBlock_deliverableItemOrder on DeliverableItemOrder {
      id
      deliverableItem {
        slug
        id
        name
        pictureUrl
      }
      ...DeliverableItemOrderFormDialog_deliverableItemOrderFragment
    }
    ${deliverableItemOrderFragment}
  ` as TypedDocumentNode<DeliverableItemBlock_deliverableItemOrder>,
};
